import { createClient } from '@supabase/supabase-js';
import { active_project } from './router.js';

const SUPABASE_URL = 'https://flljtoysvlxzwjwwwsvq.supabase.co';
const SUPABASE_KEY = 'sb_publishable_F_LfdU2JpL5K1GIYC2UH_g_NgmYe2oi';

export const sb = createClient(SUPABASE_URL, SUPABASE_KEY);

/* ═══════════════════════════════════════════
   § ANALYTICS — Session & Event Dispatcher
   ═══════════════════════════════════════════ */

// Shared UUID generator for both token types.
// crypto.randomUUID() is supported in all modern browsers.
// Falls back to a manual hex string for older environments.
function _generateUUID() {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(16).slice(2) + '-' + Date.now().toString(16);
  }
}

// ── Visitor ID (Long-Term) ─────────────────
// Persists in localStorage across sessions and tabs.
// Identifies the same physical user returning to the project.
const VISITOR_KEY = 'plotos_visitor_id';
let _visitorId = localStorage.getItem(VISITOR_KEY);
if (!_visitorId) {
  _visitorId = _generateUUID();
  localStorage.setItem(VISITOR_KEY, _visitorId);
}

// ── Session ID (Short-Term) ────────────────
// Persists in sessionStorage for this browser tab's lifetime only.
// Resets on every new tab or window — isolates a single browsing session.
const SESSION_KEY = 'plotos_session_id';
let _sessionId = sessionStorage.getItem(SESSION_KEY);
if (!_sessionId) {
  _sessionId = _generateUUID();
  sessionStorage.setItem(SESSION_KEY, _sessionId);
}

/**
 * trackEvent — Fire-and-forget telemetry dispatcher.
 *
 * Inserts one row into `project_events`. This function is intentionally
 * NOT awaited by callers so it never blocks the render loop or UI thread.
 *
 * IMPORTANT (Supabase v2): The client NEVER throws on API errors.
 * It always resolves with { data, error }. A try/catch alone will NOT
 * catch RLS failures, missing tables, or bad payloads — we MUST
 * destructure and check the `error` object explicitly.
 *
 * @param {string} eventType  — 'canvas_load' | 'plot_view' | 'whatsapp_click'
 * @param {string|null} plotId — Plot_ID string (e.g. 'Plot-042'), or null
 */
export async function trackEvent(eventType, plotId = null) {
  try {
    const payload = {
      project_id: active_project || null,
      visitor_id: _visitorId,
      session_id: _sessionId,
      event_type: eventType,
      plot_id:    plotId ?? null,
    };

    // DEBUG: log what we're sending so payload can be verified
    console.log('[Telemetry] Sending:', payload);

    const { error } = await sb.from('project_events').insert(payload);

    // Supabase v2 surfaces errors here, NOT via a thrown exception.
    if (error) {
      console.error('[Telemetry] Supabase insert error:', error.message, error);
    } else {
      console.log('[Telemetry] ✓', eventType, plotId ?? '');
    }
  } catch (err) {
    // True network-level failure (fetch itself threw — e.g. offline)
    console.error('[Telemetry] Network error:', err);
  }
}
